import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart, Scale } from "lucide-react";
import { useShortlist, useCompare } from "@/lib/listStore";
import { useSettings } from "@/context/SettingsContext";
import { safeUrl } from "@/lib/media";

const DEFAULT_TAGLINE = "We're Here to Guide You Home.";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/properties", label: "Properties" },
    { to: "/emi-calculator", label: "EMI Calculator" },
    { to: "/properties?category=Presidential+Properties", label: "Presidential" },
    { to: "/properties?category=Under+Construction", label: "Under Construction" },
    { to: "/properties?category=Ready+to+Move", label: "Ready to Move" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = React.useState(false);
    const shortlist = useShortlist();
    const compare = useCompare();
    const { settings } = useSettings();
    // The round badge; the name is written beside it. Until the settings arrive (first visit only) a blank space of
    // the same size is shown, so the built-in badge never flashes before an uploaded logo.
    const logo = settings ? (safeUrl(settings.logo_url) || "/brand/icon.svg") : "";
    const tagline = settings ? (settings.header_tagline ?? DEFAULT_TAGLINE) : DEFAULT_TAGLINE;
    const IconLink = ({ to, icon: Icon, label, count, testId }) => (
        <Link to={to} aria-label={`${label}${count ? ` (${count})` : ""}`} title={label} data-testid={testId}
            className="relative w-10 h-10 grid place-items-center rounded-full text-[var(--navy)] hover:bg-slate-100">
            <Icon size={19} />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gold)] text-[var(--navy)] text-[10px] font-semibold grid place-items-center">{count}</span>}
        </Link>
    );

    return (
        <header className="glass-header sticky top-0 z-40" data-testid="site-header">
            {tagline && (
                <div className="bg-[var(--navy)] text-center px-4 py-1.5" data-testid="header-tagline">
                    <p className="font-serif-display italic text-[15px] sm:text-base tracking-wide text-[var(--gold-light)] leading-snug">{tagline}</p>
                </div>
            )}
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="brand-logo">
                        {logo
                            ? <img src={logo} alt="" aria-hidden="true" fetchpriority="high" decoding="async" className="h-11 w-11 sm:h-12 sm:w-12 xl:h-14 xl:w-14 object-contain shrink-0" />
                            : <span aria-hidden="true" className="h-11 w-11 sm:h-12 sm:w-12 xl:h-14 xl:w-14 shrink-0" />}
                        <span className="flex flex-col justify-center min-w-0">
                            <span className="font-serif-display font-semibold text-[21px] sm:text-[24px] xl:text-[27px] leading-none tracking-[0.01em] text-[var(--navy)] whitespace-nowrap">Elegant Home Advisors</span>
                            <span className="mt-1.5 h-px w-full bg-gradient-to-r from-[var(--gold)] to-transparent" aria-hidden="true" />
                        </span>
                    </Link>

                    <nav className="hidden min-[1360px]:flex items-center gap-8">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.label}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[14px] font-medium tracking-wide whitespace-nowrap transition-colors duration-300 ${
                                        isActive ? "text-[var(--navy)]" : "text-slate-600 hover:text-[var(--navy)]"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden min-[1360px]:flex items-center gap-1">
                        <IconLink to="/shortlist" icon={Heart} label="Shortlisted properties" count={shortlist.ids.length} testId="nav-shortlist" />
                        <IconLink to="/compare" icon={Scale} label="Compare properties" count={compare.ids.length} testId="nav-compare" />
                    </div>
                    <div className="min-[1360px]:hidden flex items-center">
                        <IconLink to="/shortlist" icon={Heart} label="Shortlisted properties" count={shortlist.ids.length} testId="nav-shortlist-m" />
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 text-[var(--navy)]"
                        data-testid="mobile-menu-btn"
                        aria-label="Menu"
                        aria-expanded={open}
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                    </div>
                </div>

                {open && (
                    <div className="min-[1360px]:hidden pb-6 space-y-3" data-testid="mobile-nav">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.label}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className="block py-2 text-[15px] text-slate-700 border-b border-slate-100"
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <NavLink to="/compare" onClick={() => setOpen(false)} className="block py-2 text-[15px] text-slate-700 border-b border-slate-100">Compare properties{compare.ids.length ? ` (${compare.ids.length})` : ""}</NavLink>
                    </div>
                )}
            </div>
        </header>
    );
}
